import React from "react";
import { t } from "ttag";
import { Checkbox, DeleteIcon, IconButton, RefreshIcon, TextField, Tooltip } from "@coolremote/shared";
import styles from "./ModbusSiteLibraryManagementDialog.module.css";

interface INamedItem {
  id?: string;
  name: string;
}

export const buildCheckboxColumn = <T extends INamedItem>(
  exportMode: boolean,
  allSelected: boolean,
  toggleAll: () => void,
  selectedIds: Set<string>,
  toggleSelected: (id: string) => void,
) =>
  exportMode
    ? [{
      id: "select",
      width: "5%",
      align: "center" as const,
      title: <div style={{ display: "flex", justifyContent: "center" }}><Checkbox size="small" checked={allSelected} onChange={toggleAll} /></div>,
      render: (row: T) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Checkbox size="small" checked={!!row.id && selectedIds.has(row.id)} onChange={() => row.id && toggleSelected(row.id)} />
        </div>
      ),
    }]
    : [];

/**
 * Editable name cell.
 */
export const buildNameColumn = <T extends INamedItem>(
  title: string,
  width: string,
  editingNames: Record<string, string>,
  setDraftName: (id: string, value: string) => void,
  pendingDeleteIds: Set<string>,
) => [{
  id: "name",
  title,
  width,
  render: (row: T) => {
    const id = row.id ?? "";
    const pendingDelete = !!row.id && pendingDeleteIds.has(row.id);
    const draft = editingNames[id];
    const changed = draft !== undefined && draft.trim() !== row.name;

    return (
      <div className={styles.nameCell}>
        <Tooltip title={draft ?? row.name} placement="top">
          <span className={styles.nameFieldWrap}>
            <TextField
              fullWidth
              className={`${styles.nameField} ${pendingDelete ? styles.nameFieldDeleted : ""}`}
              value={draft ?? row.name}
              disabled={pendingDelete}
              onChange={(v: string) => row.id && setDraftName(row.id, v)}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
            />
          </span>
        </Tooltip>
        {/* Marks the row as carrying an edit that Save has not applied yet. */}
        {changed && !pendingDelete && (
          <Tooltip title={t`Unsaved change`} placement="top">
            <span className={styles.unsavedDot} />
          </Tooltip>
        )}
      </div>
    );
  },
}];

/**
 * Stages a deletion instead of performing it, and lets the user take it back.
 * Nothing is removed until Save.
 */
export const buildDeleteActionColumn = <T extends INamedItem>(
  exportMode: boolean,
  pendingDeleteIds: Set<string>,
  onToggleDelete: (id: string) => void,
  width = "10%",
) =>
  exportMode
    ? []
    : [{
      id: "action",
      width,
      align: "right" as const,
      render: (row: T) => {
        const pendingDelete = !!row.id && pendingDeleteIds.has(row.id);
        return (
          <Tooltip title={pendingDelete ? t`Undo remove` : t`Remove from library`} placement="top">
            <span>
              <IconButton disableRipple size="small" onClick={() => row.id && onToggleDelete(row.id)}>
                {pendingDelete ? <RefreshIcon /> : <DeleteIcon />}
              </IconButton>
            </span>
          </Tooltip>
        );
      },
    }];
